/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import React, { FC, useCallback, useState } from "react";
import { getExpiry, PermitType } from "../../../../utils/permitUtils";
import { GlobalStore } from "../../../../store/global/GlobalStore";
import { getPermit2Signature } from "../../../../utils/permit2Utils";
import * as Sentry from "@sentry/browser";
import { ConfirmButton } from "./ConfirmButton";
import { useApproveViaPermits } from "../../../../utils/abis/usePermits";
import { withIsVisible } from "../../../../utils/reactUtils";
import { Address, Hash } from "viem";
import { css } from "@emotion/react";
import { Text } from "../../../general/Text";
import LoadingEllipse from "../../../ButterComponents/LoadingEllipse/LoadingEllipse";
import { FlexContainer } from "../../../../containers/FlexContainer";
import { Button } from "../../../general/Button/Button";

export interface SignatureRequestProps {
  permitType: PermitType | undefined;
  approvalTarget: Hash | undefined;
  onSignatureDone: () => void;
}

enum SignatureRequestStatus {
  None = "None",
  Processing = "Processing",
  Success = "Success",
  Error = "Error",
}

const SignatureRequest: FC<SignatureRequestProps> = props => {
  const network = GlobalStore.getRawState().network;
  const quote = GlobalStore.getRawState().quote;
  const takerTokens = GlobalStore.getRawState().takerTokens;
  const [sigRequestStatus, setSigRequestStatus] = useState<SignatureRequestStatus>(
    SignatureRequestStatus.None
  );
  // TODO: Figure out how to only run this when permit type is properly defined
  const { approve } = useApproveViaPermits(props.permitType ?? "permit1", props.approvalTarget);

  const handlePermit2Signature = useCallback(
    (tokenAddresses: Array<`0x${string}`>) => {
      console.log(`Starting permit2 signature request`);
      const address = GlobalStore.getRawState().address;
      const expiry = getExpiry();
      if (props.approvalTarget) {
        getPermit2Signature({
          userAddress: address as Address,
          approvalTarget: props.approvalTarget,
          tokenAddresses: tokenAddresses,
          network,
          expiry: expiry,
        })
          .then(res => {
            console.log(`Setting permit2 on store`);
            //Update sig on store
            const tokenAddresses = GlobalStore.getRawState().quote?.requiredSignatures ?? [];
            if (res) {
              GlobalStore.update(s => {
                s.permits.permit2.approvalTarget = props.approvalTarget;
                s.permits.permit2.signature = res.signature;
                s.permits.permit2.deadline = expiry;
                s.permits.permit2.nonces = tokenAddresses.map((t, index) => {
                  return {
                    token: t,
                    nonce: res.nonces[index],
                  };
                });
              });
              console.log(`Running onSignatureDone`);
              props.onSignatureDone();
            }
          })
          .catch(error => {
            Sentry.captureException(`Could not obtain Permit2 signature due to: `, {
              extra: { e: error },
            });
            setSigRequestStatus(SignatureRequestStatus.Error);
          });
      }
    },
    [network, props]
  );
  const handlePermit1Signature = useCallback(() => {
    // Permit1 is only for 121 trades - so we *should* be safe doing this
    approve(takerTokens[0].token)
      .then(r => {
        GlobalStore.update(s => {
          s.permits.permit1.approvalTarget = props.approvalTarget;
          s.permits.permit1.signature = r?.signature;
          s.permits.permit1.deadline = r?.deadline;
        });
        props.onSignatureDone();
      })
      .catch(e => {
        console.error(e);
        setSigRequestStatus(SignatureRequestStatus.Error);
      });
  }, [approve, props, takerTokens]);

  const handleGetSignature = useCallback(() => {
    console.log(`Getting signature for permit type`, props.permitType);
    if (props.permitType) {
      switch (props.permitType) {
        case "permit1":
          handlePermit1Signature();
          break;
        case "permit2":
          handlePermit2Signature(quote?.requiredSignatures ?? []);
          break;
      }
    }
  }, [handlePermit1Signature, handlePermit2Signature, props.permitType, quote?.requiredSignatures]);
  return (
    <SignatureRequestContainer>
      <ConfirmButton
        text={"Enable for free"}
        confirm={true}
        isVisible={sigRequestStatus === SignatureRequestStatus.None}
        onClick={() => {
          console.log(`Handling signature`);
          setSigRequestStatus(SignatureRequestStatus.Processing);
          handleGetSignature();
        }}
      />
      <AnimatedConfirmInWallet isVisible={sigRequestStatus === SignatureRequestStatus.Processing} />
      {sigRequestStatus === SignatureRequestStatus.Error && (
        <Button
          onClick={() => {
            setSigRequestStatus(SignatureRequestStatus.Processing);
            handleGetSignature();
          }}
          buttonType="primary"
          textSize={"Large"}
          css={css`
            width: 340px;
            display: flex;
            padding: 19px 18px;
            justify-content: center;
            align-items: center;
            align-self: stretch;
            border-radius: 14px;
          `}
          text={"Enable for free"}
        />
      )}
    </SignatureRequestContainer>
  );
};

const SignatureRequestContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
`;

const AnimatedConfirmInWallet = withIsVisible(() => {
  const theme = GlobalStore.useState(s => s.theme);
  return (
    <FlexContainer
      css={css`
        justify-content: center;
      `}
    >
      <Button
        isDisabled={true}
        buttonCss={css`
          width: 340px;
          display: flex;
          padding: 19px 18px;
          justify-content: center;
          align-items: center;
          align-self: stretch;
          border-radius: 14px;
        `}
      >
        <LoadingEllipse color={theme.button.primary.text.disabled} />
        <Text textStyle="Button" size="Large" color={theme.button.primary.text.disabled}>
          Confirm in wallet
        </Text>
      </Button>
    </FlexContainer>
  );
});

export default withIsVisible(SignatureRequest);
